import { AreaChart } from '@mantine/charts';
import { Box, Group, Stack, Text } from '@mantine/core';
import { tr, uiLocale } from '../lib/i18n';
import { fmtNum } from '../lib/labels';
import { EmptyState, Panel } from '../ui/kit';

/** Status colors by meaning (Mantine palette names), not a decorative rainbow. */
export const PROPERTY_BAR: Record<string, string> = {
  PENDING_REVIEW: 'orange.5',
  AVAILABLE: 'teal.6',
  RESERVED: 'blue.5',
  SOLD: 'ink.9',
  RENTED: 'ink.7',
  WITHDRAWN: 'red.5',
  ARCHIVED: 'gray.4',
};

export const REQUEST_BAR: Record<string, string> = {
  NEW: 'orange.5',
  ASSIGNED: 'blue.4',
  IN_PROGRESS: 'blue.6',
  CONTACTED: 'indigo.5',
  MATCHED: 'teal.5',
  WON: 'teal.7',
  LOST: 'red.5',
  CANCELLED: 'gray.4',
};

export { Kpi } from '../ui/kit';

export type BarRow = { label: string; value: number; color?: string };

const cssColor = (c: string) => {
  const [name, shade] = c.split('.');
  return shade ? `var(--mantine-color-${name}-${shade})` : c;
};

/** A ranked list with a bar per row (largest first). Lighter and easier to read than a chart for a dozen labels. */
export function BarsCard({ title, rows, description }: { title: string; rows: BarRow[]; description?: string; height?: number }) {
  const data = [...rows].sort((a, b) => b.value - a.value).slice(0, 12);
  const max = Math.max(...data.map((d) => d.value), 1);
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <Panel title={title} description={description} h="100%">
      {data.length === 0 ? (
        <EmptyState title={tr('لا توجد بيانات في هذه الفترة', 'No data for this period')} />
      ) : (
        <Stack gap={12}>
          {data.map((d) => (
            <Box key={d.label}>
              <Group justify="space-between" gap="xs" mb={5} wrap="nowrap">
                <Text size="sm" truncate>{d.label}</Text>
                <Text size="sm" fw={600} className="pf-num" style={{ flexShrink: 0 }}>
                  {fmtNum(d.value)}
                  <Text span size="xs" c="dimmed" fw={400}> · {total ? Math.round((d.value / total) * 100) : 0}%</Text>
                </Text>
              </Group>
              <Box h={6} bg="var(--pf-hover)" style={{ borderRadius: 99, overflow: 'hidden' }}>
                <Box h="100%" w={`${(d.value / max) * 100}%`} bg={cssColor(d.color ?? 'ink.9')} style={{ borderRadius: 99, transition: 'width 300ms ease' }} />
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Panel>
  );
}

/** Daily trend for one or two entities on one axis. */
export function TrendCard({ title, days, series, description }: { title: string; days: string[]; series: { label: string; data: number[] }[]; description?: string }) {
  const colors = ['ink.9', 'gray.5'];
  const data = days.map((d, i) => ({
    day: new Date(d).toLocaleDateString(uiLocale(), { day: 'numeric', month: 'short' }),
    ...Object.fromEntries(series.map((s, si) => [`s${si}`, s.data[i] ?? 0])),
  }));
  return (
    <Panel
      title={title}
      description={description}
      actions={series.length > 1 ? (
        <Group gap="md">
          {series.map((s, i) => (
            <Group key={s.label} gap={6}><Box w={10} h={10} bg={cssColor(colors[i]!)} style={{ borderRadius: 3 }} /><Text size="xs" c="dimmed">{s.label}</Text></Group>
          ))}
        </Group>
      ) : undefined}
    >
      {days.length === 0 ? (
        <EmptyState title={tr('لا توجد بيانات في هذه الفترة', 'No data for this period')} />
      ) : (
        <AreaChart
          h={260}
          data={data}
          dataKey="day"
          series={series.map((s, i) => ({ name: `s${i}`, label: s.label, color: colors[i]! }))}
          curveType="monotone"
          withDots={days.length < 20}
          gridAxis="x"
          tickLine="none"
          strokeWidth={2}
          fillOpacity={0.12}
          valueFormatter={(v) => fmtNum(v)}
          yAxisProps={{ allowDecimals: false, width: 36 }}
          xAxisProps={{ interval: 'preserveStartEnd', minTickGap: 24 }}
        />
      )}
    </Panel>
  );
}

/** Fill every day in [from, to] so the line does not skip empty days. */
export function dayRange(from: string, to: string): string[] {
  const out: string[] = [];
  const d = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  while (d <= end && out.length < 400) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/** The status badge tone that goes with a bar color (so table and chart agree). */
export function toneOfBar(c: string | undefined): import('../ui/kit').Tone {
  const n = c?.split('.')[0];
  if (n === 'indigo') return 'violet';
  if (n === 'orange' || n === 'teal' || n === 'blue' || n === 'red') return n;
  return 'gray';
}

/** Group status rows by their label (several codes can share one). */
export function statusBars(rows: { status: string; total: number }[], labels: Record<string, string>, colors: Record<string, string>): BarRow[] {
  return rows.reduce<BarRow[]>((acc, r) => {
    const l = labels[r.status] ?? r.status;
    const e = acc.find((x) => x.label === l);
    if (e) e.value += r.total;
    else acc.push({ label: l, value: r.total, color: colors[r.status] });
    return acc;
  }, []);
}
